// Fase 4 · Enrutado de los sobres `{ seq, epoch, type, id, restauranteId,
// originDeviceId, ts, data }` del hub `/hub/tpv` a sus handlers.
//
// Eco: los eventos que ESTE dispositivo ha provocado (originDeviceId === yo)
// se descartan para los tipos de estado de mesa/pedido/catálogo — el estado
// local ya está fresco por la respuesta HTTP. Nunca se descartan:
//   - lease.*     (un takeover "revoked" me afecta aunque venga de mí en otra pestaña)
//   - payment.*   (dedupe por paymentHistoryId en el reducer)
//   - device.* / user.* / notification.* / cashdrawer.* (órdenes o avisos)

const ECHO_DROPPABLE_PREFIXES = [
  "mesa.",
  "mesas.",
  "session.",
  "pedido.",
  "dinerSplit.",
  "products.",
  "booking.",
  "kds.",
];

export function isEcho(env, myDeviceId) {
  const origin = env?.originDeviceId;
  if (!origin || !myDeviceId) return false;
  return String(origin).toLowerCase() === String(myDeviceId).toLowerCase();
}

// Excepciones dentro de las familias descartables: `pedido.created` alimenta
// la lista de pedidos (onlyOrders) también con los pedidos propios, igual que
// hacía el hub legacy `pedido/{rid}`.
const NEVER_ECHO_DROP_TYPES = ["pedido.created"];

// Eco que SÍ se entrega (con `env.echo = true`): la ficha de la mesa que manda
// el servidor tras MI propia acción es la fuente de verdad y debe ganar al
// estado optimista (p. ej. al cobrar, los comensales vuelven a 0 solo con
// ella). El handler aplica los datos y se salta los efectos (refetch, salir
// de la mesa…), que ya hizo la propia acción.
const ECHO_APPLY_DATA_TYPES = ["mesa.changed", "mesa.productsUpdated"];

export function isEchoDataApplied(type) {
  return !!type && ECHO_APPLY_DATA_TYPES.includes(type);
}

export function isEchoDroppable(type) {
  if (!type) return false;
  if (NEVER_ECHO_DROP_TYPES.includes(type)) return false;
  return ECHO_DROPPABLE_PREFIXES.some((p) => type.startsWith(p));
}

/**
 * handlers: { [type]: fn(data, env) } — admite comodín por familia
 * (`"device.*"`) para tipos que el servidor pueda ampliar.
 */
export function createEventRouter({ getMyDeviceId, handlers = {}, onUnknown } = {}) {
  const resolve = (type) => {
    if (!type) return null;
    if (handlers[type]) return handlers[type];
    const dot = type.indexOf(".");
    if (dot > 0) {
      const family = `${type.slice(0, dot)}.*`;
      if (handlers[family]) return handlers[family];
    }
    return null;
  };

  return {
    route(env) {
      if (!env || !env.type) return "invalid";
      const echo = isEcho(env, getMyDeviceId?.());
      if (echo && isEchoDroppable(env.type) && !isEchoDataApplied(env.type)) return "echo";
      const handler = resolve(env.type);
      if (!handler) {
        onUnknown?.(env);
        return "unhandled";
      }
      if (echo && isEchoDroppable(env.type)) {
        handler(env.data, { ...env, echo: true });
        return "echo-applied";
      }
      handler(env.data, env);
      return "handled";
    },
  };
}
